import { useState, useCallback, useEffect } from 'react';
import { supabase } from './useSupabase';
import { defaultPresets, type TemplatePreset, type CategorySlug, findPresetById } from '@/mocks/template-presets';
import { showToast } from '@/components/Toast';

const STORAGE_KEY = 'eletronica2000-presets';
const ACTIVE_KEY = 'eletronica2000-active-preset';

function loadLocalPresets(): TemplatePreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as TemplatePreset[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // fallback
  }
  return [...defaultPresets];
}

function loadLocalActiveId(): string | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (raw) return raw;
  } catch {
    // fallback
  }
  return null;
}

function saveLocalPresets(presets: TemplatePreset[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // ignore
  }
}

function saveLocalActiveId(id: string | null) {
  try {
    if (id) {
      localStorage.setItem(ACTIVE_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_KEY);
    }
  } catch {
    // ignore
  }
}

export function useTemplatePresets() {
  const [presets, setPresets] = useState<TemplatePreset[]>(loadLocalPresets);
  const [activeId, setActiveId] = useState<string | null>(loadLocalActiveId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('template_presets')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      if (data && data.length > 0) {
        const mapped = data.map((d) => ({
          id: d.id,
          name: d.name,
          category: d.category as CategorySlug,
          primaryColor: d.primary_color,
          secondaryColor: d.secondary_color,
          topBannerDesktop: d.top_banner_desktop,
          topBannerMobile: d.top_banner_mobile,
          mainImage: d.main_image,
          pdfCoverImage: d.pdf_cover_image,
          pdfBackCoverImage: d.pdf_back_cover_image,
          footerImage: d.footer_image,
          title: d.title,
          subtitle: d.subtitle,
          description: d.description,
          activeSpecialties: Array.isArray(d.active_specialties) ? d.active_specialties : [],
          active: d.active,
        })) as TemplatePreset[];
        setPresets(mapped);
        saveLocalPresets(mapped);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar presets');
      showToast('Erro ao carregar presets do servidor. Usando presets locais.', 'warning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const savePreset = useCallback(async (preset: TemplatePreset) => {
    const exists = presets.find((p) => p.id === preset.id);
    const payload = {
      id: preset.id,
      name: preset.name,
      category: preset.category,
      primary_color: preset.primaryColor,
      secondary_color: preset.secondaryColor,
      top_banner_desktop: preset.topBannerDesktop,
      top_banner_mobile: preset.topBannerMobile,
      main_image: preset.mainImage,
      pdf_cover_image: preset.pdfCoverImage,
      pdf_back_cover_image: preset.pdfBackCoverImage,
      footer_image: preset.footerImage,
      title: preset.title,
      subtitle: preset.subtitle,
      description: preset.description,
      active_specialties: preset.activeSpecialties as unknown as Record<string, unknown>[],
      active: preset.active,
    };
    try {
      if (exists) {
        await supabase.from('template_presets').update(payload).eq('id', preset.id);
      } else {
        await supabase.from('template_presets').insert(payload);
      }
      showToast('Preset salvo com sucesso!', 'success');
    } catch (err) {
      showToast('Erro ao salvar preset no servidor. Salvo localmente.', 'warning');
    }
    const next = exists
      ? presets.map((p) => (p.id === preset.id ? preset : p))
      : [...presets, preset];
    setPresets(next);
    saveLocalPresets(next);
  }, [presets]);

  const deletePreset = useCallback(async (id: string) => {
    try {
      await supabase.from('template_presets').delete().eq('id', id);
      showToast('Preset removido com sucesso!', 'success');
    } catch (err) {
      showToast('Erro ao remover preset no servidor. Removido localmente.', 'warning');
    }
    const next = presets.filter((p) => p.id !== id);
    setPresets(next);
    saveLocalPresets(next);
  }, [presets]);

  const setActive = useCallback((id: string | null) => {
    setActiveId(id);
    saveLocalActiveId(id);
  }, []);

  const resetToDefault = useCallback(async () => {
    setPresets([...defaultPresets]);
    setActiveId(null);
    saveLocalPresets([...defaultPresets]);
    saveLocalActiveId(null);
    showToast('Presets restaurados para o padrão.', 'info');
  }, []);

  const togglePresetActive = useCallback(async (id: string) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    const updated = { ...preset, active: !preset.active };
    try {
      await supabase.from('template_presets').update({ active: updated.active }).eq('id', id);
      showToast('Status do preset atualizado!', 'success');
    } catch (err) {
      showToast('Erro ao atualizar status no servidor. Atualizado localmente.', 'warning');
    }
    const next = presets.map((p) => (p.id === id ? updated : p));
    setPresets(next);
    saveLocalPresets(next);
  }, [presets]);

  const activePreset = activeId ? findPresetById(activeId) || undefined : undefined;

  return {
    presets,
    activeId,
    activePreset,
    loading,
    error,
    savePreset,
    deletePreset,
    setActive,
    resetToDefault,
    togglePresetActive,
  };
}

export function applyPresetToConfig<T extends { top: { bannerImage: string; title: string; subtitle: string }; mainImage: { image: string }; cover: { image: string; title: string; subtitle: string }; backCover: { image: string }; footer: { bannerImage: string }; specialties: { name: string; icon: string; active: boolean; order: number }[] }>(
  config: T,
  preset: TemplatePreset
): T {
  return {
    ...config,
    top: {
      ...config.top,
      bannerImage: preset.topBannerDesktop || config.top.bannerImage,
      title: preset.title || config.top.title,
      subtitle: preset.subtitle || config.top.subtitle,
    },
    mainImage: {
      ...config.mainImage,
      image: preset.mainImage || config.mainImage.image,
    },
    cover: {
      ...config.cover,
      image: preset.pdfCoverImage || config.cover.image,
      title: preset.title || config.cover.title,
      subtitle: preset.subtitle || config.cover.subtitle,
    },
    backCover: {
      ...config.backCover,
      image: preset.pdfBackCoverImage || config.backCover.image,
    },
    footer: {
      ...config.footer,
      bannerImage: preset.footerImage || config.footer.bannerImage,
    },
    specialties: preset.activeSpecialties.length > 0
      ? preset.activeSpecialties
      : config.specialties,
  };
}

export function getPresetForCategory(category: CategorySlug, presets: TemplatePreset[]): TemplatePreset | undefined {
  return presets.find((p) => p.category === category && p.active);
}